import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { CopyIcon, MoreVerticalIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import type { ProjectListItem } from '@/entities/project';
import { PermissionTooltip } from '@/shared/ui';
import { useTranslation } from 'react-i18next';

interface ProjectRowActionsProps {
  project: ProjectListItem;
  onEdit: (project: ProjectListItem) => void;
  onDelete: (project: ProjectListItem) => void;
  /** Row edit/delete are workspace-admin gated (project-list scope, not per-project). */
  canEdit: boolean;
  canDelete: boolean;
}

export function ProjectRowActions(props: ProjectRowActionsProps) {
  const { t } = useTranslation('projects');

  const { project, onEdit, onDelete, canEdit, canDelete } = props;

  return (
    <TooltipProvider delayDuration={300}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="mini"
            aria-label={t('projectsList.projectRowActions.projectActions')}
            className="size-9 p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVerticalIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <PermissionTooltip disabled={!canEdit}>
            <DropdownMenuItem disabled={!canEdit} onSelect={() => onEdit(project)}>
              <PencilIcon className="mr-2 size-4" />
              {t('projectsList.projectRowActions.edit')}
            </DropdownMenuItem>
          </PermissionTooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Hidden for now until post-cannes so we can introduce this feature later */}
              <DropdownMenuItem
                aria-disabled
                onSelect={(e) => e.preventDefault()}
                className="hidden data-highlighted:bg-transparent"
              >
                <CopyIcon className="mr-2 size-4" />
                {t('projectsList.projectRowActions.duplicate')}
              </DropdownMenuItem>
            </TooltipTrigger>
            <TooltipContent>{t('projectsList.projectRowActions.comingSoon')}</TooltipContent>
          </Tooltip>
          <PermissionTooltip disabled={!canDelete}>
            <DropdownMenuItem disabled={!canDelete} onSelect={() => onDelete(project)}>
              <Trash2Icon className="mr-2 size-4" />
              {t('projectsList.projectRowActions.delete')}
            </DropdownMenuItem>
          </PermissionTooltip>
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
}
